import { useEffect, useMemo, useState } from "react";
import axios from "../../../../services/axios";
import Constants from "../../../../constants/Index";
import { AlertModal } from "../../../Modal/Alert/Index";
import { ButtonRedirect } from "../../../Modal/Error/ButtonRedirect/Index";
import { SimpleButtonClose } from "../../../Modal/Contents/SimpleButtonClose/Index";
import { TextModal } from "../../../Modal/Contents/TextModal/Index";
import { TitleModal } from "../../../Modal/Contents/TitleModal/Index";
import {
  ButtonV2,
  FormCardV2,
  OptionV2,
  RemoveButtonV2,
} from "../../../FormControlsV2";
import { UserFormV2 } from "../../../FormsV2";
import { FooterEditOnboarding } from "../../../EditOnboarding/Footer";
import "./styles.css";

type ProfileUserV2 = {
  id?: string | number;
  uuid?: string | number;
  profile: OptionV2 | null;
  name: string;
  phone: string;
  isNew: boolean;
};

type ProfileUserErrorsV2 = Partial<Record<string, string>>;

const emptyUser: ProfileUserV2 = {
  profile: null,
  name: "",
  phone: "",
  isNew: true,
};

function createEmptyUser() {
  return {
    ...emptyUser,
    uuid: `new-${Date.now()}-${Math.random()}`,
  };
}

function normalizeUser(user: any, profilesList: OptionV2[]): ProfileUserV2 {
  const profile =
    profilesList.find((option) => String(option.value) === String(user.profile_id || user.profile?.value)) ||
    profilesList.find((option) => option.label === user.profile_name || option.label === user.profile?.label) ||
    {
      label: user.profile_name || user.profile?.label || "",
      value: user.profile_id || user.profile?.value || user.profile_name || "",
    };

  return {
    id: user.id,
    uuid: user.uuid || user.id,
    profile: profile.label ? profile : null,
    name: user.name || "",
    phone: user.phone || "",
    isNew: false,
  };
}

export function ProfilesUsersV2() {
  const [profilesList, setProfilesList] = useState<OptionV2[]>([]);
  const [users, setUsers] = useState<ProfileUserV2[]>([]);
  const [errors, setErrors] = useState<ProfileUserErrorsV2>({});
  const [showValidation, setShowValidation] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const existingPhones = useMemo(() => users.filter((user) => !user.isNew).map((user) => user.phone), [users]);
  const newUsers = useMemo(() => users.filter((user) => user.isNew), [users]);

  function loadUsers() {
    setLoading(true);
    axios
      .get("/users/user")
      .then(({ data }) => {
        setUsers(data.map((user: any) => normalizeUser(user, profilesList)));
      })
      .finally(() => setLoading(false));
  }

  function validate(nextUsers = users, fullValidation = showValidation) {
    const validation: ProfileUserErrorsV2 = {};
    const phoneCounter = new Map<string, number>();

    nextUsers.forEach((user) => {
      if (user.isNew && user.phone) phoneCounter.set(user.phone, (phoneCounter.get(user.phone) || 0) + 1);
    });

    nextUsers.forEach((user, index) => {
      if (!user.isNew) return;

      if (fullValidation && !user.profile) validation[`users[${index}].profile`] = "Selecione o Cargo/Função";
      if (fullValidation && !user.name.trim()) validation[`users[${index}].name`] = "*Campo obrigatório não informado.";
      if (user.name && !/^[^\s]+(\s+[^\s]+)*$/.test(user.name)) {
        validation[`users[${index}].name`] = "Escreva um nome válido";
      }
      if (user.name && user.name.length < 3) validation[`users[${index}].name`] = "Necessário ter no mínimo 3 letras";
      if (fullValidation && !user.phone) validation[`users[${index}].phone`] = "*Campo obrigatório não informado.";
      if (user.phone && user.phone.length !== 11) validation[`users[${index}].phone`] = "Telefone informado inválido!";
      if (user.phone && existingPhones.includes(user.phone)) {
        validation[`users[${index}].phone`] = "Telefone já está sendo utilizado.";
      }
      if (user.phone && phoneCounter.get(user.phone)! > 1) {
        validation[`users[${index}].phone`] = Constants.MESSAGE_UNIQUE_PHONE;
      }
    });

    setErrors(validation);
    return Object.keys(validation).length === 0;
  }

  function updateUser(index: number, partial: Partial<ProfileUserV2>) {
    const nextUsers = users.map((user, userIndex) => (userIndex === index ? { ...user, ...partial } : user));
    setUsers(nextUsers);
    if (showValidation) validate(nextUsers);
  }

  function addUser() {
    if (newUsers.length) {
      setShowValidation(true);
      if (!validate(users, true)) return;
    }

    const nextUsers = [...users, createEmptyUser()];
    setUsers(nextUsers);
    if (showValidation) validate(nextUsers);
  }

  function removeUser(index: number) {
    const nextUsers = users.filter((_, userIndex) => userIndex !== index);
    setUsers(nextUsers);
    if (showValidation) validate(nextUsers);
  }

  function cancelNewUsers() {
    setUsers(users.filter((user) => !user.isNew));
    setErrors({});
    setShowValidation(false);
  }

  function saveUsers() {
    setShowValidation(true);
    if (!newUsers.length || !validate(users, true)) return;

    const postUsers = users
      .filter((user) => user.isNew)
      .map((user) => ({
        name: user.name,
        phone: user.phone,
        profile_id: Number(user.profile?.value),
        password: Constants.DEFAULT_PASSWORD,
      }));

    setSaving(true);
    axios
      .post("/users/user", postUsers)
      .then(() => {
        setSuccessVisible(true);
        loadUsers();
      })
      .finally(() => setSaving(false));
  }

  useEffect(() => {
    axios.get("/profiles").then(({ data }) => {
      setProfilesList(data.map((profile: any) => ({ label: profile.name, value: profile.id })));
    });
  }, []);

  useEffect(() => {
    if (!profilesList.length) return;
    loadUsers();
  }, [profilesList]);

  return (
    <>
      <AlertModal visible={successVisible} contentStyle={{ width: 440, height: 230, borderRadius: 20, padding: 20 }}>
        <SimpleButtonClose onClick={() => setSuccessVisible(false)} />
        <div className="profiles-users-v2__success">
          <TitleModal>Perfis adicionados</TitleModal>
          <TextModal>Os novos usuários foram cadastrados com a senha padrão.</TextModal>
          <strong>{Constants.DEFAULT_PASSWORD}</strong>
        </div>
        <ButtonRedirect onClick={() => setSuccessVisible(false)}>Continuar</ButtonRedirect>
      </AlertModal>

      <main className="profiles-users-v2">
        <div className="profiles-users-v2__list">
          {!loading && users.length === 0 && (
            <div className="profiles-users-v2__empty">
              <strong>Nenhum usuário cadastrado.</strong>
              <span>Clique em adicionar perfil para cadastrar o primeiro usuário.</span>
            </div>
          )}

          {users.map((user, index) => (
            <div className="onboarding-v2-list-card" key={`${user.uuid || index}`}>
              <FormCardV2
                title={user.isNew ? "Novo perfil" : undefined}
                description={user.isNew ? "Preencha os dados do novo usuário." : undefined}
                actions={user.isNew ? <RemoveButtonV2 onClick={() => removeUser(index)} /> : undefined}
              >
                <UserFormV2
                  namePrefix={`users[${index}]`}
                  profile={user.profile}
                  profiles={profilesList}
                  name={user.name}
                  phone={user.phone}
                  disabled={!user.isNew}
                  errors={{
                    profile: errors[`users[${index}].profile`],
                    name: errors[`users[${index}].name`],
                    phone: errors[`users[${index}].phone`],
                  }}
                  onProfileChange={(profile) => updateUser(index, { profile })}
                  onNameChange={(name) => updateUser(index, { name })}
                  onPhoneChange={(phone) => updateUser(index, { phone })}
                />
              </FormCardV2>
            </div>
          ))}
        </div>

        <div className="profiles-users-v2__actions">
          <ButtonV2 variant="primary" onClick={addUser} disabled={saving}>
            + ADICIONAR PERFIL
          </ButtonV2>
        </div>
      </main>

      <FooterEditOnboarding
        isNew
        undoFunction={cancelNewUsers}
        saveFunction={saveUsers}
        disabledSubmit={!newUsers.length || saving || (showValidation && Object.keys(errors).length > 0)}
      />
    </>
  );
}
